import React, { useEffect, useState } from 'react'
import { useNavigate } from "react-router-dom";
import { userAPI } from "../api";
import WplButton from "../components/wpl-button/WplButton";
import RequireAuth from "./RequireAuth";
import { getUserScope } from "./scope";

// Scopes locked to their own endpoints cannot call /user/me, and are never
// admins, so they are turned away without a request.
const CLIENT_SCOPES = ['wind-diagnostics', 'rocky', 'external-goose']

const REDIRECT_SECONDS = 6

// Shown instead of the admin page, then sends the user to the home page.
export function AccessDenied({ message = 'The admin section is only available to developers.' }) {
    const navigate = useNavigate()
    const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS)

    useEffect(() => {
        if (secondsLeft <= 0) {
            navigate('/', { replace: true })
            return
        }
        const timer = setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000)
        return () => clearTimeout(timer)
    }, [secondsLeft, navigate])

    return <RequireAuth>
        <div style={{ maxWidth: 560, margin: '96px auto', padding: '0 16px', textAlign: 'center' }}>
            <h1>You don't have access to this page</h1>
            <p>{message}</p>
            <p>Redirecting to the home page in {secondsLeft} second{secondsLeft === 1 ? '' : 's'}…</p>
            <WplButton value='Go to the home page' onClick={() => navigate('/', { replace: true })} />
        </div>
    </RequireAuth>
}

// Admin section guard: developers and super admins only. Everyone else sees
// AccessDenied, then goes back to the home page. Wraps RequireAuth so the layout
// stays the same.
export default function RequireDeveloper({ children, ...authProps }) {
    const scope = getUserScope()
    const [allowed, setAllowed] = useState(scope === 'developer' ? true : null)

    useEffect(() => {
        if (scope === 'developer' || CLIENT_SCOPES.includes(scope)) return
        userAPI.me()
            .then(me => setAllowed(Boolean(me && me.is_super_admin)))
            .catch(() => setAllowed(false))
    }, [scope])

    if (scope !== 'developer' && CLIENT_SCOPES.includes(scope)) return <AccessDenied />
    if (allowed === null) return null
    if (!allowed) return <AccessDenied />

    return <RequireAuth {...authProps}>{children}</RequireAuth>
}
